import { useMutation, useQuery } from 'react-query';
import { omitQueryToUrl, parseUrlQuery, request } from '@/utils';
import { atom, useRecoilState } from 'recoil';
import { localStorageEffect } from '@/utils/recoil';
import storage from '@/utils/storage1';
import { useEffect } from 'react';
import { useCurrentAddressInfo, useUserType } from '@/hooks';
import ReactGA4 from 'react-ga4';
import message from 'antd/es/message';
import { getRecoil, setRecoil } from 'recoil-nexus';
import { omit } from 'lodash';

export const useUserReferralCode = () => {
  const { address, isVerified } = useCurrentAddressInfo();
  return useQuery(
    ['/user/ref_code', address],
    () => request.get<{ ref_code: number; id: string }>('/user/ref_code'),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!(address && isVerified),
      onError: () => {
        message.error('get referral code error');
      },
    },
  );
};

export const useValidateReferralCode = () => {
  return useMutation((refCode: number) =>
    request
      .post<{ is_valid: boolean }>('/user/ref_code/validate', { ref_code: refCode })
      .then(res => res?.is_valid),
  );
};

const referralCodeAtom = atom({
  key: 'currentReferralCode',
  default: storage.ref_code_1,
  effects_UNSTABLE: [localStorageEffect('ref_code_1')],
});

export const useLocalReferralCode = (address?: string) => {
  const [referralCode, setReferralCode] = useRecoilState<Record<string, string>>(referralCodeAtom);
  const currentKey = address || 'visitor';
  const localReferralCode = referralCode?.[currentKey];
  const setLocalReferralCode = (code: string) => {
    setReferralCode({
      ...referralCode,
      [currentKey]: code,
    });
  };
  return {
    localReferralCode,
    setLocalReferralCode,
  };
};

export const deleteRefcode = (address: string) => {
  const refCodeDict = getRecoil(referralCodeAtom);
  setRecoil(referralCodeAtom, omit(refCodeDict, address));
};

export const useReferralTrack = () => {
  const { mutate } = useMutation(({ refCode, path }: { refCode: number; path: string }) =>
    request.post<{ is_success: boolean }>('/user/ref_code/track', { ref_code: refCode, path }),
  );
  const userType = useUserType();
  const path = window.location.href;
  const { query } = parseUrlQuery(path);
  const refCode = query?.r;
  const newUrl = omitQueryToUrl(path, 'r');

  useEffect(() => {
    if (!refCode) return;
    if (Number(refCode) && userType) {
      mutate({ refCode: Number(refCode), path: newUrl });
      ReactGA4.event({
        category: 'Open Refer Link',
        action: `Open Refer Link_${userType}`,
      });
    }
  }, [mutate, newUrl, refCode, userType]);
};
